import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Box, FormControlLabel, TextField } from '@mui/material';
import { useGetValidateMdtIdQuery } from '~/api/v2/hooks';
import { InfoFormLabel } from '~/components';
import { CheckIcon } from '~/icons';
import { datasetMdtIdRules } from '~/validation/datasetSchema';
import { fieldContainerWithCheckmarkStyles } from './styles';

const MdtIdField = () => {
  const datasetId = +useParams().id!;
  const {
    register,
    watch,
    setError,
    clearErrors,
    formState: { dirtyFields, errors },
  } = useFormContext();
  const value = watch('mdt_id');
  const [validateMdtId, setValidateMdtId] = useState<string>('');
  const { data, isFetching, refetch } = useGetValidateMdtIdQuery(
    { mdt_id: validateMdtId },
    {
      skip: !validateMdtId || validateMdtId.length > datasetMdtIdRules.max || !dirtyFields.mdt_id || !!errors.mdt_id,
    },
  );

  const shouldValidate = data && !isFetching && !!validateMdtId && !data?.is_unique && dirtyFields.mdt_id;
  const validationMessage = 'Dataset ID number is already in use in HDP. The ID must be unique.';

  useEffect(() => {
    if (errors.mdt_id) {
      return;
    } else if (shouldValidate) {
      setError('mdt_id', { type: 'validate', message: validationMessage });
    } else {
      clearErrors('mdt_id');
    }
  }, [shouldValidate]);

  const mdtIdField = register('mdt_id', {
    onChange: () => {
      setValidateMdtId('');
      clearErrors('mdt_id');
    },
  });

  return (
    <FormControlLabel
      label={
        <InfoFormLabel
          label="DATASET ID NUMBER"
          tooltipText={`Optional field. If left blank, a unique Dataset ID (up to ${datasetMdtIdRules.max} characters) will be automatically generated by the system. Users may enter their own value if desired.`}
        />
      }
      control={
        <Box sx={fieldContainerWithCheckmarkStyles}>
          <TextField
            {...mdtIdField}
            disabled={!!datasetId || isFetching}
            helperText={errors.mdt_id?.message as string}
            error={!!errors.mdt_id}
            slotProps={{ htmlInput: { maxLength: datasetMdtIdRules.max } }}
            onBlur={(event) => {
              mdtIdField.onBlur(event);
              setValidateMdtId(value ?? '');
              if (data && value) refetch();
            }}
          />
          {data?.is_unique && !errors.mdt_id && !isFetching && value && (
            <CheckIcon fill="#59A719" style={{ cursor: 'default' }} />
          )}
        </Box>
      }
    />
  );
};

export default MdtIdField;
